import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Section, Heading, Badge, Button } from '../components/UI.jsx';
import { metadata, images } from '../data.js';
import { Zap, Timer, Target, CheckCircle2 } from 'lucide-react';
import { getOptimizedImageUrl } from '../lib/utils.js';

const PersonligTraening = () => {
  return (
    <div className="bg-[#F1E5DC] selection:bg-[#A8814E] selection:text-white">
      <Helmet>
        <title>EMS Træning Frederiksberg - Effektiv 20 min Træning | LK STUDIO</title>
        <meta name="description" content="Prøv EMS træning på Frederiksberg. Opnå maksimale resultater på kun 20 minutter med personlig træner og skånsom, fysioterapeutisk træning. Book nu!" />
        <link rel="canonical" href="https://lkstudioems.com/om-ems-traening" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lkstudioems.com/om-ems-traening" />
        <meta property="og:title" content="EMS Træning Frederiksberg - Effektiv 20 min Træning | LK STUDIO" />
        <meta property="og:description" content="Prøv EMS træning på Frederiksberg. Opnå maksimale resultater på kun 20 minutter med personlig træner og skånsom, fysioterapeutisk træning. Book nu!" />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/acf24aad-5bd9-4c13-ac2b-a8f96ec93620.jpg" />
        <meta property="og:site_name" content="LK STUDIO" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://lkstudioems.com/om-ems-traening" />
        <meta name="twitter:title" content="EMS Træning Frederiksberg - Effektiv 20 min Træning | LK STUDIO" />
        <meta name="twitter:description" content="Prøv EMS træning på Frederiksberg. Opnå maksimale resultater på kun 20 minutter med personlig træner og skånsom, fysioterapeutisk træning. Book nu!" />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/acf24aad-5bd9-4c13-ac2b-a8f96ec93620.jpg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "EMS Træning",
              "provider": {
                "@type": "HealthAndBeautyBusiness",
                "name": "LK STUDIO",
                "url": "https://lkstudioems.com/",
                "telephone": "+4528852282",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Engtoftevej 8",
                  "addressLocality": "Frederiksberg C",
                  "postalCode": "1816",
                  "addressCountry": "DK"
                }
              },
              "areaServed": "Frederiksberg",
              "description": "Prøv EMS træning på Frederiksberg. Opnå maksimale resultater på kun 20 minutter med personlig træner og skånsom, fysioterapeutisk træning."
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[85vh] overflow-hidden bg-[#F1E5DC] pt-32 lg:pt-44 pb-16 md:pb-24">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Badge className="mb-8">
                Effektiv • Skånsom • Personlig
              </Badge>
              <h1 className="font-serif text-5xl font-normal leading-[0.9] tracking-[-0.05em] text-[#1C1B1A] sm:text-7xl lg:text-[7.5rem]">
                Fysioterapeutisk <br />
                <span className="text-[#9A8E81] italic">EMS Træning</span>
              </h1>
              
              <div className="mt-12 grid gap-10 border-t border-[#CEBDAA] pt-12 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="max-w-2xl">
                  <p className="text-xl leading-relaxed text-[#494540] sm:text-2xl">
                    Som fysioterapeut ser jeg dine specifikke behov og udfordringer. Jeg skræddersyr et træningsprogram tilpasset dine mål, så du får det optimale ud af din træning.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <Button 
                      variant="primary" 
                      onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}
                    >
                      BOOK DIN PRØVETIME
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        const el = document.getElementById('benefits');
                        el?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      LÆS MERE
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Subtle background element */}
        <div className="absolute right-0 top-0 -z-10 h-full w-1/2 overflow-hidden opacity-30 lg:opacity-80">
          <div className="relative h-full w-full">
            <img 
              src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/a344da06-6d31-4787-bb60-24a3e77037af.jpeg", { width: 1200, quality: 80 })} 
              className="w-full h-full object-cover grayscale-[0.4]" 
              alt="EMS Træning i LK STUDIO" 
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F1E5DC] via-[#F1E5DC]/50 to-transparent" />
          </div>
        </div>
      </section>

      {/* How it works */}
      <Section id="benefits" className="bg-white">
        <div className="max-w-6xl mx-auto">
          <Heading subtitle="Processen" className="mb-12 md:mb-20">Hvordan foregår <br/> træningen?</Heading>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 md:p-8 bg-[#F1E5DC] rounded-[3rem] border border-[#CEBDAA]/30">
              <div className="w-12 h-12 bg-[#1C1B1A] text-white rounded-xl flex items-center justify-center mb-6 font-black text-xl">1</div>
              <h3 className="text-lg font-black uppercase mb-4 text-[#1C1B1A]">Omklædning</h3>
              <p className="text-[#494540] font-medium leading-relaxed">
                Du er iført et Xbody undertøjssæt (leggings og t-shirt). Du bliver iført en dragt med elektroder, der sender impulser til alle store muskelgrupper.
              </p>
            </div>
            <div className="p-6 md:p-8 bg-[#F1E5DC] rounded-[3rem] border border-[#CEBDAA]/30">
              <div className="w-12 h-12 bg-[#1C1B1A] text-white rounded-xl flex items-center justify-center mb-6 font-black text-xl">2</div>
              <h3 className="text-lg font-black uppercase mb-4 text-[#1C1B1A]">20 Min. Træning</h3>
              <p className="text-[#494540] font-medium leading-relaxed">
                Du bliver guidet gennem 10-15 øvelser tilpasset dit niveau. Vi øger impulserne løbende for at udfordre musklerne mest muligt.
              </p>
            </div>
            <div className="p-6 md:p-8 bg-[#F1E5DC] rounded-[3rem] border border-[#CEBDAA]/30 md:col-span-2 lg:col-span-1">
              <div className="w-12 h-12 bg-[#1C1B1A] text-white rounded-xl flex items-center justify-center mb-6 font-black text-xl">3</div>
              <h3 className="text-lg font-black uppercase mb-4 text-[#1C1B1A]">Opfølgning</h3>
              <p className="text-[#494540] font-medium leading-relaxed">
                Computeren gemmer dine data, så vi kan bygge videre på dit niveau ved næste træning. De fleste mærker forskel efter få gange.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Results & Benefits */}
      <Section>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-20 md:py-12">
          <div className="md:w-1/2 flex flex-col items-start text-left w-full md:max-w-none">
            <Badge>DOKUMENTERET EFFEKT</Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black uppercase mb-8 leading-tight text-[#1C1B1A]">Resultater du <br/> kan mærke</h2>
            <div className="space-y-6 w-full">
              {[
                { icon: Zap, text: "Aktiverer 90% af dit styrkepotentiale" },
                { icon: Timer, text: "Tidsbesparende – kun 20 minutter" },
                { icon: Target, text: "Personlig træner ved hver session" },
                { icon: CheckCircle2, text: "Mærkbar forskel efter ca. 7-10 gange" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 justify-start">
                  <div className="w-10 h-10 bg-[#CEBDAA]/20 rounded-full flex items-center justify-center text-[#A8814E] shrink-0">
                    <item.icon className="w-5 h-5" />
                  </div>
                  <p className="text-base sm:text-lg font-bold text-[#494540]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 w-full flex flex-col items-start">
               <Button variant="primary" className="h-12 px-6 sm:h-16 sm:px-10 rounded-2xl text-sm sm:text-base" onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}>START MED EN PRØVETRÆNING</Button>
               <p className="mt-4 text-sm text-[#9A8E81] font-bold uppercase tracking-widest">Kun 150,- for din første gang</p>
            </div>
          </div>
          <div className="md:w-1/2 w-full max-w-lg mx-auto md:max-w-none">
            <div className="relative">
              <div className="aspect-[4/5] rounded-[3rem] md:rounded-[4rem] overflow-hidden shadow-2xl">
                <img 
                  src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/acf24aad-5bd9-4c13-ac2b-a8f96ec93620.jpg", { width: 800, quality: 80 })} 
                  alt="EMS Resultater" 
                  className="w-full h-full object-cover" 
                  loading="lazy"
                />
              </div>
              <motion.div 
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                whileHover={{ scale: 1.1 }}
                className="absolute -top-4 -right-4 md:-top-8 md:-right-8 lg:-top-10 lg:-right-10 w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 bg-[#F1E5DC] rounded-full border-4 md:border-6 lg:border-8 border-white flex items-center justify-center shadow-xl z-20 cursor-pointer"
              >
                <p className="text-center font-black text-[10px] lg:text-xs uppercase leading-tight tracking-widest text-[#1C1B1A]">Tilmeld dig <br/> nu</p>
              </motion.div>
            </div>
          </div>
        </div>
      </Section>

      {/* Contraindications */}
      <Section className="bg-[#CEBDAA]/20 text-[#1C1B1A] py-12 md:py-20 lg:py-32 rounded-t-[3rem] border-t border-[#CEBDAA]/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-black uppercase mb-12">Hvem kan træne EMS?</h2>
          <p className="text-lg text-[#494540] font-medium mb-12 leading-relaxed">
            EMS er ideel til alle mellem 18 og 80+ år. Det er dog kontraindiceret ved visse lidelser. Du kan ikke træne EMS hvis du lider af:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-xs sm:text-sm font-bold uppercase tracking-widest">
            <div className="p-3 border border-[#CEBDAA]/30 rounded-2xl bg-white/50">Epilepsi</div>
            <div className="p-3 border border-[#CEBDAA]/30 rounded-2xl bg-white/50">Kræft</div>
            <div className="p-3 border border-[#CEBDAA]/30 rounded-2xl bg-white/50">Diabetes</div>
            <div className="p-3 border border-[#CEBDAA]/30 rounded-2xl bg-white/50">Hjertesygdomme</div>
            <div className="p-3 border border-[#CEBDAA]/30 rounded-2xl bg-white/50">Pacemaker</div>
            <div className="p-3 border border-[#CEBDAA]/30 rounded-2xl bg-white/50">Graviditet</div>
            <div className="p-3 border border-[#CEBDAA]/30 rounded-2xl bg-white/50">Infektioner</div>
            <div className="p-3 border border-[#CEBDAA]/30 rounded-2xl bg-white/50">Nervesygdomme</div>
          </div>
          <p className="mt-12 text-sm text-[#9A8E81] font-medium italic">
            Er du i tvivl, anbefales det altid at spørge din læge inden du prøver EMS.
          </p>
        </div>
      </Section>
    </div>
  );
};

export default PersonligTraening;
